import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNow } from '../hooks/useNow.js';
import { formatAbsRel } from '../lib/time.js';
import { useTRPC } from '../lib/trpc.js';

const ICON: Record<string, string> = {
  PLAYER_JOINED: '🐝',
  ALLIANCE_FOUNDED: '🏛',
  ALLIANCE_DISSOLVED: '🏚',
  WAR_DECLARED: '⚔',
  BIG_FIGHT: '💥',
  QUEEN_KILLED: '👑',
  HIVE_TAKEN: '🏠',
  COLONY_FOUNDED: '🌱',
  WINTER_BEGINS: '❄',
  SPRING_BEGINS: '🌼',
};

/** Weltweiter Ticker (Konzept 9) mit Siegbedingung: eine Zeile, aufklappbar. */
export function TickerBar({ worldId }: { worldId: number }) {
  const trpc = useTRPC();
  const now = useNow(30_000);
  const [open, setOpen] = useState(false);
  const ticker = useQuery({
    ...trpc.world.ticker.queryOptions({ worldId, limit: 20 }),
    refetchInterval: 120_000,
  });
  const worlds = useQuery(trpc.world.list.queryOptions());
  const world = worlds.data?.find((w) => w.id === worldId);
  const latest = ticker.data?.[0];
  return (
    <div className="min-w-0 flex-1 rounded-lg bg-stone-950/40 px-3 py-1.5 text-xs">
      <button
        type="button"
        className="flex w-full items-center gap-2 text-left"
        onClick={() => setOpen((o) => !o)}
        title="Welt-Ticker"
      >
        <span className="shrink-0 text-stone-500">Ticker</span>
        <span className="min-w-0 flex-1 truncate text-stone-300">
          {latest ? `${ICON[latest.kind] ?? '•'} ${latest.text}` : 'Noch nichts passiert.'}
        </span>
        <span className="text-stone-500">{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div className="mt-2 flex flex-col gap-1 border-t border-stone-800 pt-2">
          {world?.victoryCondition && (
            <p className="mb-1 rounded-md bg-amber-950/40 px-2 py-1 text-amber-200">
              🏆 {world.victoryCondition}
            </p>
          )}
          {ticker.data?.map((t) => (
            <div key={t.id} className="flex gap-2 text-stone-300">
              <span className="shrink-0">{ICON[t.kind] ?? '•'}</span>
              <span className="min-w-0 flex-1">{t.text}</span>
              <span className="shrink-0 text-stone-500">{formatAbsRel(t.createdAt, now)}</span>
            </div>
          ))}
          {ticker.data?.length === 0 && <p className="text-stone-500">Noch nichts passiert.</p>}
        </div>
      )}
    </div>
  );
}
